import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Sparkles, Eye, EyeOff } from "lucide-react";
import { saveSessionToken } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");
  const [successMessage, setSuccessMessage] = useState("");
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      console.log("Login successful:", response.data);
      // Save session token to localStorage for mobile support
      if (response.data.session_token) {
        saveSessionToken(response.data.session_token);
      }
      navigate("/dashboard", { state: { user: response.data }, replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!credentials.username) {
      setError("Please enter your username or email first");
      return;
    }
    
    setIsForgotLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { username: credentials.username });
      setSuccessMessage(response.data.message || "If an account exists, a new password has been sent to the admin.");
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to process forgot password request. Please try again.";
      setError(errorMsg);
    } finally {
      setIsForgotLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%), url('https://images.pexels.com/photos/13063307/pexels-photo-13063307.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Logo Section - Mobile First */}
      <div className="flex-shrink-0 pt-8 pb-4 px-4 text-center">
        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/95 backdrop-blur-md rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
          <span className="text-4xl sm:text-5xl font-bold bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] bg-clip-text text-transparent">M</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white drop-shadow-lg tracking-wide">MELA</h1>
        <h2 className="text-xl sm:text-2xl font-semibold text-[#F6D003] mt-1 drop-shadow-md tracking-wider">PANIPURI</h2>
        <p className="text-white/90 mt-3 text-sm sm:text-base max-w-xs mx-auto drop-shadow-sm">
          Savor the nostalgia of street food with modern hygiene
        </p>
      </div>

      {/* Login Card - Scrollable on mobile */}
      <div className="flex-1 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-8">
        <Card className="w-full max-w-md border-0 shadow-2xl rounded-3xl overflow-hidden bg-white/95 backdrop-blur-md">
          <CardHeader className="text-center pb-2 pt-6 px-4 sm:px-6">
            <CardTitle className="text-xl sm:text-2xl font-bold text-gray-800">Welcome Back</CardTitle>
            <CardDescription className="text-sm">Sign in to your franchisee account</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {error && (
              <Alert variant="destructive" className="mb-4 border-[#F2337B]/30 bg-[#F2337B]/5">
                <AlertCircle className="h-4 w-4 text-[#F2337B]" />
                <AlertDescription className="text-[#F2337B] text-sm">{error}</AlertDescription>
              </Alert>
            )}
            {successMessage && (
              <Alert className="mb-4 border-green-500/30 bg-green-50">
                <AlertDescription className="text-green-700 text-sm">{successMessage}</AlertDescription>
              </Alert>
            )}

            <Tabs defaultValue="google" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6 bg-gray-100/80 rounded-xl p-1 h-11">
                <TabsTrigger 
                  value="google" 
                  data-testid="google-tab"
                  className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:shadow-md"
                >
                  Google
                </TabsTrigger>
                <TabsTrigger 
                  value="credentials" 
                  data-testid="credentials-tab"
                  className="rounded-lg text-sm data-[state=active]:bg-white data-[state=active]:shadow-md"
                >
                  Username
                </TabsTrigger>
              </TabsList>

              <TabsContent value="google">
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Sign in with your registered Google account
                  </p>
                  <Button
                    data-testid="google-login-btn"
                    onClick={handleGoogleLogin}
                    className="w-full h-12 sm:h-14 bg-white text-foreground border-2 border-gray-200 hover:border-[#F2337B] hover:bg-[#F2337B]/5 rounded-xl transition-all duration-200 text-sm sm:text-base"
                  >
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="truncate">Continue with Google</span>
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="credentials">
                <form onSubmit={handleCredentialLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-sm font-medium">Username or Email</Label>
                    <Input
                      id="username"
                      data-testid="username-input"
                      type="text"
                      placeholder="Enter your username"
                      value={credentials.username}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                      className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#F2337B] focus:ring-[#F2337B]/20 text-base"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        data-testid="password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={credentials.password}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#F2337B] focus:ring-[#F2337B]/20 text-base pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isForgotLoading}
                      className="text-sm text-[#F2337B] hover:underline disabled:opacity-50"
                      data-testid="forgot-password-link"
                    >
                      {isForgotLoading ? "Sending..." : "Forgot Password?"}
                    </button>
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    className="w-full h-12 sm:h-14 rounded-xl bg-gradient-to-r from-[#F2337B] to-[#FF6B9D] hover:opacity-90 shadow-lg shadow-[#F2337B]/30 transition-all duration-200 text-base"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
              <p className="text-xs text-muted-foreground text-center">
                Only registered franchisees can access this portal.
                <br />
                <span className="text-[#F2337B]">Contact admin if you need access.</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
